/**
 * @lines98/client/components/skin-gallery — под-экран «Оформление» (UI вариант 1a,
 * appearance-skins.md §5). Монтируется как drill-down ВНУТРИ settings-panel (НЕ
 * страница рейла) → оверлей поверх доски, фаза не меняется, партия не сбрасывается.
 *
 * СТРУКТУРА (§5.0 layout-plan, must-visible):
 *   1. Закреплённое сверху ЖИВОЕ превью-мини-доска текущей комбинации (палитра ×
 *      материал) — обновляется мгновенно при выборе свотча.
 *   2. Вкладки [Доска | Шары] — примитив `components/segmented` (role=tablist,
 *      клавиатура-стрелки, тактильные 44px зашиты внутрь). НЕ самодельные контролы.
 *   3. Сетка свотчей активной вкладки — ОДИН переиспользуемый ствол `lib/swatch-
 *      gallery` (обе вкладки монтируют его с разными данными, не копией — AC-9).
 *
 * ДВЕ ОСИ из реестра `lib/skin-registry` (BOARD_PALETTES × BALL_STYLES). Выбор
 * персистится сразу в `client-prefs` (boardSkin = палитра, ballStyle = материал) →
 * боевые доски/реплеи перекрашиваются реактивно через `lib/board-appearance`
 * (subscribePrefs), этот под-экран только пишет prefs + обновляет своё превью.
 *
 * NB exported name `mountSkinGallery` сохранён (settings-drawer зовёт его) — это
 * исторический id под-экрана; внутри он теперь двух-осевой «Оформление».
 *
 * CSS — co-located `skin-gallery.css`.
 */

import './skin-gallery.css';

import { startState } from '@lines98/shared';

import { applyAppearance, currentAppearance } from '../lib/board-appearance.js';
import { mountBoardView } from '../lib/board-view.js';
import { getClientPrefs, setPref } from '../lib/client-prefs.js';
import { t } from '../lib/i18n/index.js';
import {
  BALL_STYLES,
  BOARD_PALETTES,
  type BallStyleDef,
  type PaletteDef,
} from '../lib/skin-registry.js';
import {
  mountBallsSample,
  mountSwatchGallery,
  type SwatchGalleryHandle,
} from '../lib/swatch-gallery.js';

import { createSegmented, type SegmentedHandle } from './segmented.js';

/** Фиксированный представительный seed для закреплённого превью (static, не зачётный). */
const PREVIEW_SEED = 0x10b98;

const TAB_PALETTE = 'palette';
const TAB_BALL = 'ball';

export interface SkinGalleryHandle {
  /** Корневой элемент под-экрана — потребитель (панель) вставляет в свой под-экран. */
  readonly element: HTMLElement;
  /**
   * Пере-резолвить локализованные подписи (вкладки / aria / имена свотчей) на смену
   * языка. Внутренности построены с `t()`-строками, запечёнными в DOM при сборке
   * (имена свотчей — через `def.name` getter реестра); чтобы освежить язык,
   * перестраиваем тело галереи целиком (выбор осей persist в client-prefs — не
   * теряется). Вызывает потребитель (settings-drawer) из своей locale-подписки.
   */
  relabel(): void;
  destroy(): void;
}

/** Внутренняя сборка тела галереи (element + destroy) — пересобирается на смену языка. */
interface GalleryBody {
  readonly element: HTMLElement;
  destroy(): void;
}

/**
 * Построить под-экран «Оформление». Возвращает handle со СТАБИЛЬНЫМ внешним
 * контейнером `element` (потребитель вставляет его раз) + `relabel()` (пере-сборка
 * тела на смену языка) + `destroy()`. `onSelect` (опц.) дёргается при смене любой
 * оси (для будущего живого превью на доске позади; боевая доска и так реактивна
 * через board-appearance).
 */
export function mountSkinGallery(opts?: { onSelect?: () => void }): SkinGalleryHandle {
  // Стабильный внешний контейнер — НЕ перестраивается (потребитель держит ссылку);
  // меняется только его единственный ребёнок-тело при relabel().
  const outer = document.createElement('div');
  outer.className = 'skin-gallery-outer';

  let body = buildGalleryBody(opts);
  outer.appendChild(body.element);

  return {
    element: outer,
    relabel(): void {
      const next = buildGalleryBody(opts);
      outer.appendChild(next.element);
      body.destroy(); // снять старое тело (board-view ресурсы) после вставки нового
      body = next;
    },
    destroy(): void {
      body.destroy();
      if (outer.parentNode) outer.parentNode.removeChild(outer);
    },
  };
}

/** Собрать тело галереи (превью + вкладки + сетки свотчей) с актуальными `t()`-строками. */
function buildGalleryBody(opts?: { onSelect?: () => void }): GalleryBody {
  const root = document.createElement('div');
  root.className = 'skin-gallery';

  // ── 1. Закреплённое превью — осе-зависимое (owner-directive 2026-06-16: оси не
  //    смешиваются). Вкладка «Доска» → мини-доска БЕЗ шаров; «Шары» → 7 шаров БЕЗ доски.
  const previewHost = document.createElement('div');
  previewHost.className = 'appearance-preview';

  // Превью доски (фон/клетки палитры, без шаров).
  const previewBoardWrap = document.createElement('div');
  previewBoardWrap.className = 'appearance-preview-board';
  previewHost.appendChild(previewBoardWrap);
  const previewBoard = mountBoardView(previewBoardWrap, {
    interactive: false,
    ariaLabel: t('game.appearance.boardAria'),
  });
  const emptyGrid = startState(PREVIEW_SEED).grid.map((row) => row.map(() => 0 as const));
  previewBoard.setScore(0);
  previewBoard.setMoves(0);
  previewBoard.renderNiche([], false);
  previewBoard.renderStaticGrid(emptyGrid, undefined);

  // Превью шаров (7 шаров в материале, без доски).
  const previewBallsWrap = document.createElement('div');
  previewBallsWrap.className = 'appearance-preview-balls';
  previewHost.appendChild(previewBallsWrap);
  const previewBalls = mountBallsSample(previewBallsWrap);

  const repaintPreview = (): void => {
    const sel = currentAppearance();
    // Доска: только палитра (фон/клетки) — шары на превью доски не показываем.
    applyAppearance(previewBoard.frame, { paletteId: sel.paletteId, ballStyleId: 'classic' });
    // Шары: материал на 7-шаровый образец.
    previewBalls.setAttribute('data-ball-style', sel.ballStyleId);
  };
  repaintPreview();

  // ── 2. Вкладки [Доска | Шары] — примитив segmented (a11y/тактильные зашиты) ───
  let activeTab = TAB_PALETTE;
  const tabs: SegmentedHandle = createSegmented({
    items: [
      { id: TAB_PALETTE, label: t('game.appearance.tabPalette') },
      { id: TAB_BALL, label: t('game.appearance.tabBall') },
    ],
    activeId: activeTab,
    ariaLabel: t('game.appearance.axesAria'),
    className: 'appearance-tabs',
    onChange: (id) => {
      activeTab = id;
      showActiveTab();
    },
  });

  // ── 3. Сетки свотчей обеих осей — ОДИН ствол lib/swatch-gallery ───────────────
  const tabBody = document.createElement('div');
  tabBody.className = 'appearance-tabbody';

  // Свотч ПАЛИТРЫ = эта палитра × ТЕКУЩИЙ материал; выбор → boardSkin.
  const paletteGallery: SwatchGalleryHandle = mountSwatchGallery({
    mode: 'board',
    items: BOARD_PALETTES.map(toSwatch),
    selectedId: getClientPrefs().boardSkin,
    ariaLabel: t('game.appearance.palettesAria'),
    appearanceFor: (paletteId) => ({
      paletteId,
      ballStyleId: getClientPrefs().ballStyle,
    }),
    onSelect: (id) => {
      setPref('boardSkin', id);
      // Превью свотчей материала зависят от текущей палитры → освежить их тоже.
      ballGallery.refreshPreviews();
      repaintPreview();
      opts?.onSelect?.();
    },
  });

  // Свотч МАТЕРИАЛА = ТЕКУЩАЯ палитра × этот материал; выбор → ballStyle.
  const ballGallery: SwatchGalleryHandle = mountSwatchGallery({
    mode: 'balls',
    items: BALL_STYLES.map(toSwatch),
    selectedId: getClientPrefs().ballStyle,
    ariaLabel: t('game.appearance.materialsAria'),
    appearanceFor: (ballStyleId) => ({
      paletteId: getClientPrefs().boardSkin,
      ballStyleId,
    }),
    onSelect: (id) => {
      setPref('ballStyle', id);
      // Превью свотчей палитр зависят от текущего материала → освежить их тоже.
      paletteGallery.refreshPreviews();
      repaintPreview();
      opts?.onSelect?.();
    },
  });

  // role=tabpanel-обёртки на каждую ось (a11y вкладок, AC-10).
  const palettePanel = wrapPanel(paletteGallery.element, TAB_PALETTE);
  const ballPanel = wrapPanel(ballGallery.element, TAB_BALL);
  tabBody.append(palettePanel, ballPanel);

  const showActiveTab = (): void => {
    const palActive = activeTab === TAB_PALETTE;
    palettePanel.hidden = !palActive;
    ballPanel.hidden = palActive;
    // Превью тоже осе-зависимое: доска на вкладке «Доска», 7 шаров на «Шары».
    previewBoardWrap.hidden = !palActive;
    previewBallsWrap.hidden = palActive;
    tabs.setActive(activeTab);
  };

  root.append(previewHost, tabs.element, tabBody);
  showActiveTab();

  return {
    element: root,
    destroy(): void {
      tabs.destroy();
      paletteGallery.destroy();
      ballGallery.destroy();
      previewBoard.unmount();
      if (root.parentNode) root.parentNode.removeChild(root);
    },
  };
}

/** Реестр-запись (палитра/материал) → общий SwatchItem (id/name/premium/badge). */
function toSwatch(def: PaletteDef | BallStyleDef): {
  id: string;
  name: string;
  premium?: boolean;
  badge?: string;
} {
  const item: { id: string; name: string; premium?: boolean; badge?: string } = {
    id: def.id,
    name: def.name,
  };
  if (def.premium !== undefined) item.premium = def.premium;
  if (def.badge !== undefined) item.badge = def.badge;
  return item;
}

/** Обернуть сетку оси в role=tabpanel (a11y вкладок). */
function wrapPanel(grid: HTMLElement, tabId: string): HTMLElement {
  const panel = document.createElement('div');
  panel.className = 'appearance-tabpanel';
  panel.setAttribute('role', 'tabpanel');
  panel.dataset.tab = tabId;
  panel.appendChild(grid);
  return panel;
}
